<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="450">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input" @submit.prevent="submitForm">
            <v-card style="position: relative;">
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4 v-if="card">Update Sim Card</h4>
                    <h4 v-else>New Sim Card</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>
                <v-overlay :model-value="!formReady" contained class="align-center justify-center">
                    <v-progress-circular indeterminate color="primary"/>
                </v-overlay>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col cols="12">
                            <v-select v-model="formData['sim_card_type_id']"
                                      :items="cardTypes" :rules="[vRules.required]"
                                      density="compact" item-title="vendor_name"
                                      item-value="id" label="Vendor"/>

                            <v-select v-model="formData['type']"
                                      :items="types" :rules="[vRules.required]"
                                      density="compact" label="Type"/>

                            <v-text-field v-model="formData['iccid']" autofocus
                                          :rules="[vRules.required,vRules.maxLength(20)]"
                                          density="compact" label="ICCID"/>
                            <v-text-field v-model="formData['eid']"
                                          :rules="[vRules.required,vRules.maxLength(32)]"
                                          density="compact" label="EID"/>
                            <v-text-field v-model="formData['imsi']"
                                          :rules="[vRules.required,vRules.maxLength(16)]"
                                          density="compact" label="IMSI"/>
                            <v-text-field v-model="formData['msisdn']"
                                          :rules="[vRules.required,vRules.maxLength(16)]"
                                          density="compact" label="MSISDN"/>
                            <v-date-input
                                v-model="formData['last_received_date']" density="compact"
                                hide-actions label="Last Received Date" prepend-icon=""/>
                            <v-date-input
                                v-model="formData['purchased_date']" density="compact"
                                hide-actions label="Purchased Date" prepend-icon=""/>
                            <v-date-input
                                v-model="formData['expiry_date']" density="compact"
                                label="Expiry Date" prepend-icon=""/>

                            <v-select v-model="formData['status']"
                                      :items="[{value:0,title:'inactive'},{value:1,title:'Active'}]"
                                      :rules="[vRules.required]"
                                      density="compact" label="Type"/>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {VDateInput} from 'vuetify/labs/VDateInput'
import {useSnackbarStore} from "@/stores/snackBarStore";

const formReady = ref(false);
const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    card: Object,
});
const formData = reactive({
    valid: false,
    type: props.card?.type ?? null,
    iccid: props.card?.iccid ?? null,
    eid: props.card?.eid ?? null,
    imsi: props.card?.imsi ?? null,
    msisdn: props.card?.msisdn ?? null,
    sim_card_type_id: props.card?.sim_card_type_id ?? null,
    last_received_date: props.card ? new Date(props.card.last_received_date) : null,
    purchased_date: props.card ? new Date(props.card.purchased_date) : null,
    expiry_date: props.card ? new Date(props.card.expiry_date) : null,
    status: props.card?.status ?? 1,
});
const types = [
    {value: 'E', title: 'Embedded'},
    {value: 'N', title: 'Nano'},
    {value: 'M', title: 'Micro'},
    {value: 'R', title: 'Regular'},
];
const emits = defineEmits(['update:modelValue', 'addCard', 'updateCard']);
const dialogSwitch = ref(props.modelValue);
const cardTypes = ref([]);
const errors = ref([]);

onMounted(() => {
    Promise.all([fetchCardTypes()]).finally(() => formReady.value = true);
})

const fetchCardTypes = () => {
    return send('get', `admin/sim-card-types`, null)
        .then((response) => {
            if (response.data.success) {
                cardTypes.value = response.data.data;
            }
        })
        .catch(function (errorResponse) {
            console.log(errorResponse)
            snackbar.showSnackbar('Sim card types could not fetched', 'error');
        })
}

const submitForm = () => {
    errors.value = [];
    if (!formData['last_received_date'] || !formData['purchased_date'] || !formData['expiry_date']) {
        snackbar.showSnackbar('All fields are required', 'error');
        return;
    }
    send(`${props.card ? 'put' : 'post'}`,
        `admin/sim-cards${props.card ? '/' + props.card.id : ''}`,
        {
            last_received_date: formatDate(formData['last_received_date']),
            purchased_date: formatDate(formData['purchased_date']),
            expiry_date: formatDate(formData['expiry_date']),
            ...formData
        })
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits(props.card ? 'updateCard' : 'addCard', response.data.data);
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response?.data?.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response?.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}

function formatDate(dateObj) {
    return `${dateObj.getFullYear()}-${dateObj.getMonth() + 1}-${dateObj.getDate()}`;
}
</script>
